import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useConsultation } from "@/context/ConsultationContext";
import { BUSINESS } from "@/data/content";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/process", label: "Process" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { openConsultation } = useConsultation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 w-full z-50 transition-all duration-500 velvet border-b border-gold/25 ${
        scrolled ? "py-1.5 shadow-[0_10px_40px_rgba(0,0,0,0.45)]" : "py-3"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 shrink-0 group">
          <img src="/logo-monogram.png" alt="West Ouachita Cabinets" className="h-12 sm:h-14 w-auto rounded-[2px] ring-1 ring-gold/30 transition-transform duration-500 group-hover:scale-105" />
          <span className="hidden sm:block font-serif leading-none">
            <span className="block text-xl tracking-wide gold-gradient-text">West Ouachita</span>
            <span className="block text-[0.6rem] tracking-luxe uppercase text-gold/70 font-sans mt-1">Fine Custom Cabinetry</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
              className={({ isActive }) =>
                `text-[0.7rem] xl:text-xs tracking-wider uppercase font-medium transition-colors duration-300 whitespace-nowrap ${
                  isActive ? "text-gold" : "text-ivory/80 hover:text-gold"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BUSINESS.phoneHref}
            data-testid="header-call"
            className="hidden 2xl:flex items-center gap-2 text-ivory/80 hover:text-gold text-sm transition-colors whitespace-nowrap"
          >
            <Phone className="h-4 w-4" />
            <span className="hidden xl:inline">{BUSINESS.phone}</span>
          </a>
          <button
            data-testid="header-cta"
            onClick={openConsultation}
            className="hidden sm:block bg-gold text-navy-deep font-semibold tracking-wider uppercase text-[0.7rem] px-5 py-3 rounded-none hover:bg-gold-light transition-colors duration-300"
          >
            Book Free 3D Consultation
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-ivory p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-navy border-t border-gold/20 mt-2">
          <nav className="flex flex-col px-6 py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `py-3 text-sm tracking-wider uppercase border-b border-gold/10 ${
                    isActive ? "text-gold" : "text-ivory/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <button
              data-testid="mobile-cta"
              onClick={openConsultation}
              className="mt-4 bg-gold text-navy-deep font-semibold tracking-wider uppercase text-xs px-5 py-4 rounded-none"
            >
              Book Free 3D Consultation
            </button>
            <a href={BUSINESS.phoneHref} className="mt-3 flex items-center justify-center gap-2 text-ivory/80 py-3 text-sm">
              <Phone className="h-4 w-4" /> {BUSINESS.phone}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
